import React from "react";
import { FaCogs, FaTools, FaMicrochip } from "react-icons/fa";

const IndustrialGridQuotation = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        expiryDate,
        items,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-slate-50 p-1 font-mono text-slate-800 min-h-[1100px] flex flex-col justify-between italic">
            <div className="bg-white border-2 border-slate-900 m-8 p-12 shadow-[15px_15px_0px_0px_rgba(15,23,42,1)] flex-1 italic">
                {/* Header */}
                <div className="flex justify-between items-start border-b-4 border-slate-900 pb-10 mb-10 italic">
                    <div className="space-y-4 italic">
                        <div className="flex items-center gap-3 italic">
                            <div className="w-14 h-14 bg-slate-900 flex items-center justify-center text-white text-3xl italic">
                                <FaCogs />
                            </div>
                            <h1 className="text-3xl font-black uppercase tracking-tighter text-slate-900 border-b-2 border-slate-900 pb-1 italic font-black whitespace-nowrap overflow-hidden text-ellipsis underline underline-offset-4 decoration-slate-900 decoration-1">{companyDetails?.name}</h1>
                        </div>
                        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest bg-slate-100 p-3 border-l-4 border-slate-900 italic shadow-inner">Origin: {companyDetails?.address}</div>
                    </div>

                    <div className="text-right flex flex-col items-end italic">
                        <div className="bg-slate-900 text-white px-8 py-3 text-sm font-black uppercase tracking-widest mb-4 italic shadow-lg">
                            PROPOSAL REF: {title}
                        </div>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[10px] font-black uppercase tracking-widest text-slate-400 italic font-bold">
                            <div>Release Date</div>
                            <div className="text-slate-900 font-black">{date}</div>
                            <div>Validity End</div>
                            <div className="text-emerald-600 font-black underline underline-offset-4 decoration-emerald-100 decoration-2">{expiryDate}</div>
                        </div>
                    </div>
                </div>

                {/* Client Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 divide-x-2 divide-slate-100 border-b-2 border-slate-900 mb-10 pb-10 italic">
                    <div className="pr-10 italic">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2 italic">Client Link</h4>
                        <div className="text-xl font-black text-slate-900 tracking-tighter uppercase italic underline underline-offset-4 decoration-slate-900 decoration-4">{to}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 italic font-black">{clientAddress}</div>
                    </div>

                    <div className="px-10 italic">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2 italic">Contact Channel</h4>
                        <div className="text-xs font-black text-slate-900 space-y-1 italic underline underline-offset-2 decoration-slate-50 decoration-1 font-black underline-offset-4">
                            <div>{clientEmail || "N/A"}</div>
                            <div>{clientPhone || "N/A"}</div>
                        </div>
                    </div>

                    <div className="pl-10 italic">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 opacity-50 underline decoration-slate-900 underline-offset-4 decoration-2 italic">Provider Channel</h4>
                        <div className="text-xs font-black text-slate-900 space-y-1 italic underline underline-offset-2 decoration-slate-50 decoration-1 font-black underline-offset-4">
                            <div>{companyDetails?.email}</div>
                            <div>{companyDetails?.phone}</div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-10 overflow-hidden rounded-xl border-2 border-slate-900 shadow-xl shadow-slate-200">
                    <table className="w-full text-left bg-white italic font-bold">
                        <thead className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest italic font-bold underline underline-offset-4 decoration-slate-400 decoration-2">
                            <tr>
                                <th className="p-6 italic">Engineering Component / Specification</th>
                                <th className="p-6 text-center border-l border-white/10 italic">Units</th>
                                <th className="p-6 text-right border-l border-white/10 italic">Unit Est.</th>
                                <th className="p-6 text-right border-l border-white/10 italic underline underline-offset-4 decoration-white/20 decoration-2">Sectional Sub.</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y-2 divide-slate-100 italic text-xs uppercase tracking-tighter leading-loose font-black">
                            {(items || []).map((item, idx) => (
                                <tr key={idx} className="group hover:bg-slate-900 hover:text-white transition-all italic whitespace-nowrap overflow-hidden text-ellipsis">
                                    <td className="p-6 border-r-2 border-slate-50 italic">
                                        <div className="text-lg font-black tracking-tighter uppercase italic underline underline-offset-4 decoration-slate-900 decoration-4 group-hover:decoration-white font-black">{item.item}</div>
                                        <div className="text-[9px] font-bold opacity-50 uppercase tracking-widest mt-2 italic font-black group-hover:opacity-100">Spec Ref: 0X-{idx + 701} — Confirmed</div>
                                    </td>
                                    <td className="p-6 text-center text-slate-400 font-bold italic">{item.qty}</td>
                                    <td className="p-6 text-right text-slate-900 font-black italic group-hover:text-white">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-6 text-right text-lg font-black text-slate-900 bg-slate-50 border-l border-slate-100 italic group-hover:bg-slate-800 group-hover:text-white font-black underline underline-offset-4 decoration-slate-100 decoration-1">
                                        Nu. {parseFloat(item.amount).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals Section */}
                <div className="grid grid-cols-2 gap-10 italic">
                    <div className="space-y-6 italic">
                        <div className="bg-slate-100 p-8 border-2 border-slate-900 shadow-[8px_8px_0px_0px_rgba(226,232,240,1)] relative overflow-hidden group italic">
                            <FaMicrochip className="absolute top-0 right-0 text-slate-200 text-6xl -mr-4 -mt-4 opacity-50 group-hover:rotate-[30deg] transition-transform duration-1000 italic" />
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-900 mb-4 items-center flex gap-2 italic underline underline-offset-4 decoration-slate-900 decoration-2">
                                <FaTools /> Technical Assumptions / Remarks
                            </h4>
                            <p className="text-[10px] font-black leading-loose text-slate-600 uppercase tracking-widest italic pr-10 font-bold italic underline underline-offset-4 decoration-slate-50 decoration-1 whitespace-nowrap overflow-hidden text-ellipsis">
                                {notes || "This estimate is generated based on current system resource availability. Any variation in component cost will be reflected in the final invoice."}
                                <br /><br />
                                {terms || "Execution starts upon 50% mobilization fee receipt."}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4 italic font-black">
                        <div className="bg-white border-2 border-slate-900 p-8 relative overflow-hidden italic shadow-2xl shadow-slate-100">
                            <div className="space-y-4 font-black uppercase tracking-widest text-[10px] italic leading-loose">
                                <div className="flex justify-between border-b-2 border-slate-50 pb-2 text-slate-400 italic">
                                    <span>Engineering Sub-Total</span>
                                    <span>Nu. {parseFloat(subTotal).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-b-2 border-slate-50 pb-2 text-slate-400 italic">
                                    <span>Regulatory Tax Provision ({taxRate}%)</span>
                                    <span>Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between border-b-2 border-slate-50 pb-2 text-emerald-600 italic underline underline-offset-4 decoration-emerald-100 decoration-4">
                                        <span>Enterprise Volume Deduction</span>
                                        <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="pt-6 mt-6 border-t-8 border-slate-900 flex justify-between items-end italic">
                                    <div className="space-y-1 italic">
                                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900 underline underline-offset-8 decoration-slate-900 decoration-4 italic underline-offset-12 leading-none whitespace-nowrap overflow-hidden text-ellipsis">Cumulative Value</div>
                                        <div className="text-5xl font-black text-slate-900 tracking-tighter italic font-black leading-none pt-4 underline underline-offset-4 decoration-slate-100 decoration-1">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-12 border-t-4 border-slate-900 pt-10 flex justify-between items-end italic">
                    <div className="bg-slate-900 text-white p-6 shadow-[10px_10px_0px_0px_rgba(203,213,225,1)] italic">
                        <h4 className="text-[9px] font-black uppercase tracking-[0.4em] mb-4 text-slate-400 underline underline-offset-4 decoration-slate-700 decoration-2 italic">Financial Settlement Node</h4>
                        <div className="text-xs font-black tracking-tighter space-y-2 uppercase italic leading-none whitespace-nowrap overflow-hidden text-ellipsis">
                            <div className="underline underline-offset-2 decoration-slate-700 decoration-2 font-black italic">{companyDetails?.bankName || "Authorized Settlement Gateway"}</div>
                            <div className="text-slate-400 tracking-[.1em] text-[10px] italic font-black">ACC NODE: {companyDetails?.accountNo || "-"}</div>
                        </div>
                    </div>

                    <div className="text-right border-r-8 border-slate-900 pr-10 italic">
                        <div className="text-[9px] font-black uppercase tracking-[0.5em] text-slate-200 mb-10 italic font-bold">Lead Engineer Certification</div>
                        <div className="w-48 h-[3px] bg-slate-900 mb-2 italic shadow-md"></div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis italic underline underline-offset-4 decoration-slate-50 decoration-1">CERT ID: SIG-EST-{"{PRO-99}"}</div>
                    </div>
                </div>
            </div>

            <div className="px-10 pb-10 flex justify-between items-center text-[8px] font-black uppercase tracking-[0.6em] text-slate-300 italic font-bold">
                <div>Proposal Stream Optimized via NexrBook Enterprise Core</div>
                <div className="bg-slate-900 text-white px-4 py-1 tracking-[0.8em] italic font-black underline underline-offset-2 decoration-slate-100 decoration-1 shadow-sm">ESTIMATION UNIT # {Math.floor(Math.random() * 100000)}</div>
            </div>
        </div>
    );
};

export default IndustrialGridQuotation;
